import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interests = [
  "Cross-Border Advisory",
  "Project Oversight",
  "PropTech & Retainer",
  "Full Portfolio Engagement",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please provide your name, email, and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      toast.success("Request received. We'll respond within one business day.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email office@dira-llc.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F9F8F6] relative overflow-hidden">
      <div
        className="absolute -top-40 right-0 w-[520px] h-[520px] rounded-full opacity-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(140,115,93,0.18) 0%, rgba(140,115,93,0) 70%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.28em] text-[#6E5A4E]">Begin a Conversation</span>
            <h2 className="font-serif-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#2B211A] leading-[1.05]">
              Let&rsquo;s build a quieter, <span className="italic text-[#8C735D]">stronger</span> portfolio.
            </h2>
            <p className="mt-7 text-[#6E5A4E] text-lg leading-relaxed font-light max-w-md">
              Send a short note and we&rsquo;ll respond within one business day. All conversations are confidential
              and there is no fee for the initial discovery call.
            </p>

            <div className="mt-12 space-y-6">
              <Row label="Email" value="office@dira-llc.com" href="mailto:office@dira-llc.com" testid="contact-email" />
              <Row label="Phone" value="+1 866 803 1434" href="tel:+18668031434" testid="contact-phone" />
              <Row
                label="Registered Office"
                value={"30 N Gould St Ste R\nSheridan, WY 82801, USA"}
                testid="contact-address"
              />
              <Row label="Practice Focus" value="London W1D · Tier-1 Metropolises" testid="contact-focus" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#EFEBE4] border border-[#D6CDBB] p-8 md:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <Field
                  id="name"
                  label="Your name"
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  testid="contact-input-name"
                />
                <Field
                  id="email"
                  type="email"
                  label="Email"
                  value={form.email}
                  onChange={onChange("email")}
                  required
                  testid="contact-input-email"
                />
                <Field
                  id="phone"
                  label="Phone (optional)"
                  value={form.phone}
                  onChange={onChange("phone")}
                  testid="contact-input-phone"
                />
                <div>
                  <label htmlFor="interest" className="block text-[11px] uppercase tracking-[0.28em] text-[#6E5A4E]">
                    Area of interest
                  </label>
                  <select
                    id="interest"
                    data-testid="contact-input-interest"
                    value={form.interest}
                    onChange={onChange("interest")}
                    className="mt-3 w-full bg-transparent border-b border-[#D6CDBB] focus:border-[#8C735D] outline-none py-2 text-[#2B211A] font-body text-[15px] appearance-none rounded-none"
                  >
                    <option value="">Select an option</option>
                    {interests.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-[11px] uppercase tracking-[0.28em] text-[#6E5A4E]">
                    Brief message
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={onChange("message")}
                    required
                    placeholder="Tell us about your portfolio, jurisdictions, and what you'd like to address."
                    className="mt-3 w-full bg-transparent border-b border-[#D6CDBB] focus:border-[#8C735D] outline-none py-2 text-[#2B211A] placeholder:text-[#6E5A4E]/70 font-body text-[15px] resize-none"
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <p className="text-xs text-[#6E5A4E] max-w-sm leading-relaxed">
                  By submitting, you agree to be contacted regarding your enquiry. We never share your details
                  with third parties.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-3 bg-[#2B211A] hover:bg-[#8C735D] disabled:opacity-60 disabled:cursor-not-allowed text-[#F9F8F6] px-8 py-4 rounded-full text-sm font-medium font-body transition-colors duration-300"
                >
                  {submitting ? "Sending…" : submitted ? "Send another" : "Request Consultation"}
                  <span aria-hidden>→</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] uppercase tracking-[0.28em] text-[#6E5A4E]">
        {label}{required && <span className="text-[#8C735D]"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-3 w-full bg-transparent border-b border-[#D6CDBB] focus:border-[#8C735D] outline-none py-2 text-[#2B211A] font-body text-[15px]"
      />
    </div>
  );
}

function Row({ label, value, href, testid }) {
  const content = (
    <>
      <div className="text-[11px] uppercase tracking-[0.28em] text-[#6E5A4E]">{label}</div>
      <div className="font-serif-display text-xl text-[#2B211A] mt-1 whitespace-pre-line group-hover:text-[#8C735D] transition-colors">
        {value}
      </div>
    </>
  );
  return (
    <div className="border-t border-[#D6CDBB] pt-4" data-testid={testid}>
      {href ? (
        <a href={href} className="group block">{content}</a>
      ) : (
        content
      )}
    </div>
  );
}
